import {Inject, Injectable, Injector, Optional} from '@angular/core';
import {AngularFirestore, AngularFirestoreCollection, CollectionReference} from "@angular/fire/compat/firestore";
import {AbstractDatabaseObject} from "../models/abstract-database-object";
import {Observable} from "rxjs";
import {map, tap} from "rxjs/operators";
import {Timestamp} from "firebase/firestore";
import firebase from "firebase/compat";
import Query = firebase.firestore.Query;
import {PageModel} from "../models/table-configuration";
import {BusinessEventService} from "./business-event.service";

@Injectable({
  providedIn: 'root'
})
export abstract class AbstractDatabaseService<T extends AbstractDatabaseObject> {

  protected collection: AngularFirestoreCollection<T>;
  protected injector?: Injector;

  protected constructor(@Inject('path') protected path: string,
                        protected afs: AngularFirestore,
                        injector?: Injector) {
    this.collection = this.afs.collection(path);
    this.injector = injector;
  }

  public get(objectId: string): Observable<T> {
    return this.collection
      .doc<T>(objectId)
      .snapshotChanges()
      .pipe(
        map(doc => {
          if (doc.payload.exists) {
            const data = doc.payload.data() as any;
            const id = doc.payload.id;
            return {id, ...data};
          }
        })
      );
  }

  public list(): Observable<T[]> {
    return this.collection
      .snapshotChanges()
      .pipe(
        map(changes => {
          return changes.map(a => {
            const data = a.payload.doc.data() as T;
            data.id = a.payload.doc.id;
            return data;
          });
        })
      );
  }



  public listByUserId(userId: string): Observable<T[]> {
    return this.afs.collection<T>(this.path, ref => ref.where('userId', '==', userId))
      .snapshotChanges()
      .pipe(
        map(changes => {
          return changes.map(a => {
            const data = a.payload.doc.data() as T;
            data.id = a.payload.doc.id;
            return data;
          });
        })
      );
  }

  /* TODO - this date logic is terrible. This function is only used by TT lists, we need to increase the day by 1 for anything coming from those datepickers */
  public listTrackingToolsByDateRange(userId: string, dateProperty: string, startDate?: Date, endDate?: Date, pageModel?: PageModel<T>) {
    return this.afs.collection<T>(this.path, ref => {
      let query: CollectionReference | Query = ref;

      query = query.where('userId', '==', userId);

      if (startDate) {
        // Reset to the start of the day in local time
        startDate.setHours(0, 0, 0, 0);
        startDate.setDate(startDate.getDate() + 1);
        // Convert to UTC
        const startDateUTC = new Date(startDate.toISOString());
        query = query.where(dateProperty, ">=", Timestamp.fromDate(startDateUTC));
      }

      if (endDate) {
        // Reset to the end of the day in local time
        endDate.setHours(23, 59, 59, 999);
        endDate.setDate(endDate.getDate() + 1);
        // Convert to UTC
        const endDateUTC = new Date(endDate.toISOString());
        query = query.where(dateProperty, "<=", Timestamp.fromDate(endDateUTC));
      }

      return this.paginateAndFilterQuery(query, pageModel);
    })
    .snapshotChanges()
    .pipe(
      map(changes => {
        return changes.map(a => {
          const data = a.payload.doc.data() as T;
          data.id = a.payload.doc.id;
          return data;
        });
      })
    );
  }

  public listByPageModel(pageModel: PageModel<T>): Observable<T[]> {
    return this.afs.collection<T>(this.path, ref => {
      return this.paginateAndFilterQuery(ref, pageModel);
    })
      .snapshotChanges()
      .pipe(
        map(changes => {
          return changes.map(a => {
            const data = a.payload.doc.data() as T;
            data.id = a.payload.doc.id;
            return data;
          });
        })
      );
  }

  add(object: T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      // Log attempt (no ID yet since object hasn't been created)
      this.logDatabaseEvent('BusinessObjectCreateAttempt', 'attempt', `Creating ${this.getEntityTypeName()}`, undefined);

      this.collection.add(object).then(ref => {
        const newItem = {
          id: ref.id,
          ...(object as any)
        };
        
        // Log success (now we have the ID)
        this.logDatabaseEvent('BusinessObjectCreateSuccess', 'success', `Successfully created ${this.getEntityTypeName()}`, newItem.id);
        
        resolve(newItem);
      }).catch(error => {
        // Log failure (no ID since creation failed)
        this.logDatabaseEvent('BusinessObjectCreateFailed', 'failure', `Failed to create ${this.getEntityTypeName()}`, undefined, {
          code: error.code || 'UNKNOWN_ERROR',
          messageSafe: error.message || 'Unknown error occurred'
        });
        reject(error);
      });
    });
  }

  update(object: T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (!object.id) {
        reject(new Error('Cannot update object without id'));
        return;
      }

      // Log attempt
      this.logDatabaseEvent('BusinessObjectUpdateAttempt', 'attempt', `Updating ${this.getEntityTypeName()}`, object.id);

      const docRef = this.collection
        .doc<T>(object.id)
        .set(object)
        .then(() => {
          // Log success
          this.logDatabaseEvent('BusinessObjectUpdateSuccess', 'success', `Successfully updated ${this.getEntityTypeName()}`, object.id);
          
          resolve({
            ...(object as any)
          });
        })
        .catch(error => {
          // Log failure
          this.logDatabaseEvent('BusinessObjectUpdateFailed', 'failure', `Failed to update ${this.getEntityTypeName()}`, object.id, {
            code: error.code || 'UNKNOWN_ERROR',
            messageSafe: error.message || 'Unknown error occurred'
          });
          reject(error);
        });
    });
  }

  delete(id: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Log attempt
      this.logDatabaseEvent('BusinessObjectDeleteAttempt', 'attempt', `Deleting ${this.getEntityTypeName()}`, id);

      const docRef = this.collection.doc<T>(id)
        .delete()
        .then(() => {
          // Log success
          this.logDatabaseEvent('BusinessObjectDeleteSuccess', 'success', `Successfully deleted ${this.getEntityTypeName()}`, id);
          
          resolve();
        })
        .catch(error => {
          // Log failure
          this.logDatabaseEvent('BusinessObjectDeleteFailed', 'failure', `Failed to delete ${this.getEntityTypeName()}`, id, {
            code: error.code || 'UNKNOWN_ERROR',
            messageSafe: error.message || 'Unknown error occurred'
          });
          reject(error);
        });
    });
  }

  public getLatest(userId: string, latestProp: keyof T = 'dateCreated'): Observable<T | null> {
    return this.afs.collection<T>(this.path, ref => ref.where('userId', '==', userId)
      .orderBy(latestProp.toString(), 'desc').limit(1))
      .snapshotChanges()
      .pipe(
        map(changes => {
          if (changes.length > 0) {
            const data = changes[0].payload.doc.data() as T;
            data.id = changes[0].payload.doc.id;
            return data;
          } else {
            return null; // or throw an error or handle this case as per your requirement
          }
        })
      );
  }

  private paginateAndFilterQuery(query: CollectionReference | Query, pageModel?: PageModel<T>): CollectionReference | Query  {
    if (pageModel) {
      if (pageModel.sortProperty && pageModel.sortOrder) {
        query = query.orderBy(pageModel.sortProperty.toString(), pageModel.sortOrder);
      }
      if (pageModel.count) {
        query = query.limit(pageModel.count);
      }
      if (pageModel.lastDocument) {
        query = query.startAfter(pageModel.lastDocument);
      }
    }
    return query;
  }

  /**
   * Get a human-readable entity type name from the collection path
   */
  private getEntityTypeName(): string {
    // Convert collection name to a readable format
    // e.g., "cashFlows" -> "CashFlow", "spendingPlans" -> "SpendingPlan"
    const name = this.path.charAt(0).toUpperCase() + this.path.slice(1);
    // Remove trailing 's' if plural
    return name.endsWith('s') ? name.slice(0, -1) : name;
  }

  /**
   * Log a business event (only if BusinessEventService is available and this is not BusinessEventService itself)
   */
  private logDatabaseEvent(
    eventType: 'BusinessObjectCreateAttempt' | 'BusinessObjectCreateSuccess' | 'BusinessObjectCreateFailed' |
               'BusinessObjectUpdateAttempt' | 'BusinessObjectUpdateSuccess' | 'BusinessObjectUpdateFailed' |
               'BusinessObjectDeleteAttempt' | 'BusinessObjectDeleteSuccess' | 'BusinessObjectDeleteFailed',
    status: 'attempt' | 'success' | 'failure',
    summary: string,
    entityId?: string,
    error?: { code: string; messageSafe: string }
  ): void {
    // Skip logging if this is BusinessEventService itself (to avoid infinite loops)
    if (this.path === 'events' || !this.injector) {
      return;
    }

    // Use setTimeout to defer logging until after initialization is complete
    // This breaks the circular dependency during service initialization
    setTimeout(() => {
      try {
        // Lazily get BusinessEventService to avoid circular dependencies
        const businessEventService = this.injector!.get(BusinessEventService, null);
        if (!businessEventService) {
          return;
        }

        // Log asynchronously to avoid blocking the main operation
        businessEventService.logEvent({
          eventType,
          status,
          summary,
          entity: {
            type: this.getEntityTypeName(),
            ...(entityId ? { id: entityId } : {}) // Only include id if it exists
          },
          error: error ? {
            code: error.code,
            messageSafe: error.messageSafe
          } : undefined
        }).catch(logError => {
          // Silently fail logging to avoid breaking the main operation
          console.error('Failed to log business event:', logError);
        });
      } catch (error) {
        // Silently fail if BusinessEventService is not available (e.g., during circular dependency resolution)
        // This can happen during initialization, so we just skip logging
      }
    }, 0);
  }
}
